import { FC, useEffect, useMemo, useState } from "react";
import { SiteItem, SuccinctSite } from "@vviinn/user-managment-client-ts";

import { useAuth } from "../../auth";
import { Restricted } from "../../components/Restricted";
import { ListWrapper, ListSettings } from "../../ui/layout/listPages";
import { SiteCardWrapper } from "../../site";
import { isPermitted } from "../../helpers/permissions/featurePermission";

import {
  useAppSelector,
  fetchDetailedSites,
  setIsExtendedCard,
} from "../../store";
import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";

const LIST_TITLE = "Sites";
const SINGLE_ITEM_TITLE = "site";

const selectedSitesQuantity = (
  sites: SuccinctSite[],
  lowercaseSearch: string,
) => {
  const filteredSites = lowercaseSearch
    ? sites.filter((site) =>
        site.domain.toLowerCase().includes(lowercaseSearch),
      )
    : sites;
  return filteredSites.length;
};

export const Sites: FC<React.PropsWithChildren<{}>> = () => {
  const { sites } = useAppSelector((state) => state.sites);
  const { loading } = useAppSelector((state) => state.sites);

  const company = useAppSelector((state) => state.company.company);
  const actualPermissions = company?.featurePermissions;
  const sitesLimitReached = company?.sitesLimitReached;

  const { getApi, notify, dispatch } = useRequestHandlers();
  const { user } = useAuth();

  const [search, setSearch] = useState("");
  const [sitesInDetails, setSitesInDetails] = useState<SiteItem[] | undefined>(
    undefined,
  );

  const selectedCompanyId = useAppSelector(
    (state) => state.company.company?.id,
  );
  const { isExtendedCard } = useAppSelector((state) => state.ui.sites);

  const getSitesInDetails = async () => {
    if (!selectedCompanyId) return;

    const api = await getApi();
    const details = await dispatch(
      fetchDetailedSites({
        api,
        value: selectedCompanyId,
      }),
    );

    setSitesInDetails(details.payload as SiteItem[]);
  };

  useEffect(() => {
    if (!user || !selectedCompanyId) return;
    getSitesInDetails();
  }, [user, selectedCompanyId]);

  const lowercaseSearch = useMemo(() => search.toLowerCase(), [search]);
  const isRestricted = useMemo(
    () =>
      !(actualPermissions
        ? isPermitted(actualPermissions, "site:advanced_config:view")
        : false),
    [actualPermissions],
  );

  const sitesToRender: SiteItem[] | SuccinctSite[] =
    sitesInDetails ?? sites ?? [];

  const newSitesNotAllowed = useMemo(() => {
    const disableCreateButtonTitle = sitesLimitReached
      ? "To increase the available number of sites upgrade your plan"
      : undefined;
    return {
      onDisabled: disableCreateButtonTitle
        ? () => notify("Info", disableCreateButtonTitle)
        : undefined,
      disableCreateButtonTitle,
    };
  }, [sitesLimitReached, notify]);

  return (
    <ListWrapper
      title={LIST_TITLE}
      newItemTitle={SINGLE_ITEM_TITLE}
      createPermissions="site:site:create"
      {...newSitesNotAllowed}
    >
      <ListSettings
        title={LIST_TITLE.toLowerCase()}
        quantity={selectedSitesQuantity(sitesToRender, lowercaseSearch)}
        initExtended={isExtendedCard}
        handleExtended={(value) => {
          dispatch(setIsExtendedCard({ feature: "sites", value }));
        }}
        initSearch={search}
        searchMessage="by domain"
        handleSearch={setSearch}
      />
      <Restricted permissions="site:list:view">
        {sitesToRender.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 lg:grid-cols-2">
            {selectedCompanyId &&
              sitesToRender.map((site) => (
                <SiteCardWrapper
                  key={site.id}
                  companyId={selectedCompanyId}
                  isExtended={isExtendedCard}
                  site={site}
                  siteDetails={
                    sitesInDetails &&
                    sitesInDetails.find(
                      (siteDetails) => siteDetails.id === site.id,
                    )
                  }
                  lowercaseSearch={lowercaseSearch}
                  isRestricted={isRestricted}
                />
              ))}
          </div>
        )}
        {sites.length === 0 && sitesInDetails?.length === 0 && !loading && (
          <p className="text-primary">You have not connected any sites yet.</p>
        )}
      </Restricted>
    </ListWrapper>
  );
};
